import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Crop, Maximize2, X } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { DEFAULT_FOCUS, useImageFocus } from "@/lib/imageFocus";

const parse = (value) => {
  if (!value || value === "contain") return { x: 50, y: 25 };
  const [x, y] = value.replace(/%/g, "").split(" ").map(Number);
  return { x: Number.isFinite(x) ? x : 50, y: Number.isFinite(y) ? y : 25 };
};

/** Small button that opens the framing editor for one image URL. */
export const FocusButton = ({ url, aspect = "aspect-[4/3]", testId, focusKey }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  if (!url) return null;
  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 border border-[#C69A3B] px-3 py-1.5 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
      >
        <Crop size={12} /> {t("admin.framing")}
      </button>
      {open && <FocusEditor url={url} focusKey={focusKey || url} aspect={aspect} onClose={() => setOpen(false)} />}
    </>
  );
};

const FocusEditor = ({ url, focusKey, aspect, onClose }) => {
  const { t } = useI18n();
  const { focusOf, saveFocus } = useImageFocus();
  const initial = focusOf(focusKey);
  const [contain, setContain] = useState(initial === "contain");
  const [pos, setPos] = useState(parse(initial));
  const [dragging, setDragging] = useState(false);
  const boxRef = useRef(null);

  const move = useCallback((clientX, clientY) => {
    const rect = boxRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      x: Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100)),
      y: Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100)),
    });
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (e) => {
      const p = e.touches ? e.touches[0] : e;
      move(p.clientX, p.clientY);
    };
    const stop = () => setDragging(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
    };
  }, [dragging, move]);

  const persist = async (value) => {
    try {
      await saveFocus(focusKey, value);
      toast.success(t("admin.siteSaved"));
      onClose();
    } catch {
      toast.error(t("admin.framingError"));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="focus-editor-modal"
      className="fixed inset-0 z-[90] grid place-items-center bg-[#161616]/95 p-4"
    >
      <div className="w-full max-w-xl border border-[#C69A3B]/50 bg-[#FAFAF7] p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="label-mono">{t("admin.framing")}</p>
            <p className="mt-2 text-xs text-[#5A5752]">{t("admin.framingHelp")}</p>
          </div>
          <button
            type="button"
            data-testid="focus-editor-close"
            aria-label={t("nav.close")}
            onClick={onClose}
            className="grid h-10 w-10 shrink-0 place-items-center border border-[#C69A3B]/60 text-[#8F3329]"
          >
            <X size={16} />
          </button>
        </div>

        <div
          ref={boxRef}
          data-testid="focus-editor-preview"
          onMouseDown={(e) => { if (!contain) { setDragging(true); move(e.clientX, e.clientY); } }}
          onTouchStart={(e) => { if (!contain) { setDragging(true); move(e.touches[0].clientX, e.touches[0].clientY); } }}
          className={`mt-5 w-full overflow-hidden border border-[#C69A3B]/40 bg-[#EFEAE0] ${aspect} ${contain ? "" : "cursor-move"}`}
        >
          <img
            src={url}
            alt=""
            draggable={false}
            className="h-full w-full"
            style={{
              objectFit: contain ? "contain" : "cover",
              objectPosition: contain ? "center" : `${pos.x}% ${pos.y}%`,
            }}
          />
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="focus-toggle-contain"
            onClick={() => setContain((c) => !c)}
            className={`inline-flex min-h-[44px] items-center gap-2 border px-4 text-xs transition-colors duration-300 ${
              contain ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B] text-[#8F3329]"
            }`}
          >
            <Maximize2 size={13} /> {t("admin.framingContain")}
          </button>
          <button
            type="button"
            data-testid="focus-reset"
            onClick={() => { setContain(false); setPos(parse(DEFAULT_FOCUS)); }}
            className="min-h-[44px] border border-[#C69A3B] px-4 text-xs text-[#8F3329]"
          >
            {t("admin.framingReset")}
          </button>
          <button
            type="button"
            data-testid="focus-save"
            onClick={() => persist(contain ? "contain" : (Math.round(pos.x) === 50 && Math.round(pos.y) === 25 ? null : `${Math.round(pos.x)}% ${Math.round(pos.y)}%`))}            className="min-h-[44px] border border-[#8F3329] bg-[#8F3329] px-5 text-xs text-[#FAFAF7] transition-colors duration-300 hover:bg-[#252525]"
          >
            {t("btn.save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FocusButton;
